import { useState, useEffect, useCallback, useRef } from 'react';
import { presetService } from '../services/presetService';

interface PresetNameValidationResult {
  isValid: boolean;
  error: string | null;
  isChecking: boolean;
  checkNameAvailability: (name: string) => void;
}

// Simple debounce implementation
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/**
 * Custom hook for validating preset names in real-time
 * Checks for duplicates and provides appropriate error messages
 */
export const usePresetNameValidation = (
  initialName = '',
  excludeId?: string // For editing - exclude current preset's ID
): PresetNameValidationResult => {
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const validateName = useCallback(async (name: string) => {
    // Reset state
    setError(null);
    setIsValid(true);
    
    // Skip validation for empty names (let form validation handle required fields)
    if (!name || name.trim() === '') {
      return;
    }

    // Basic validation
    if (name.length < 2) {
      setError('Name must be at least 2 characters long');
      setIsValid(false);
      return;
    }

    if (name.length > 100) {
      setError('Name must be less than 100 characters');
      setIsValid(false);
      return;
    }

    // Check for valid characters (alphanumeric, spaces, hyphens, underscores)
    const validNamePattern = /^[a-zA-Z0-9\s\-_]+$/;
    if (!validNamePattern.test(name)) {
      setError('Name can only contain letters, numbers, spaces, hyphens, and underscores');
      setIsValid(false);
      return;
    }

    // Check for duplicate names
    setIsChecking(true);
    try {
      const exists = await presetService.checkPresetNameExists(name.trim(), excludeId);
      
      if (exists) {
        setError('A preset with this name already exists. Please choose a different name.');
        setIsValid(false);
      } else {
        setIsValid(true);
      }
    } catch (error) {
      console.warn('Error checking preset name availability:', error);
      // In case of error, we'll allow the name but let backend handle final validation
      setIsValid(true);
    } finally {
      setIsChecking(false);
    }
  }, [excludeId]);

  // Debounced version to avoid too many API calls
  const debouncedValidate = useCallback(
    debounce((name: string) => validateName(name), 500),
    [validateName]
  );

  const checkNameAvailability = useCallback((name: string) => {
    debouncedValidate(name);
  }, [debouncedValidate]);

  // Initial validation if a name is provided
  useEffect(() => {
    if (initialName) {
      checkNameAvailability(initialName);
    }
  }, [initialName, checkNameAvailability]);

  return {
    isValid,
    error,
    isChecking,
    checkNameAvailability,
  };
}; 